import {
  CreateHatchCoverMoveCommand,
  EquipmentPlannedDto,
  EquipmentType,
  HatchCoverMoveDirection,
  HatchCoverMoveDto,
  UpdateHatchCoverMoveCommand,
} from '@operations/app/api'
import { EventTypes } from '@operations/messages/eventsTypes'
import equipmentService from '@operations/services/equipmentService'
import hatchCoverMoveService from '@operations/services/hatchCoverMoveService'
import { IEvent, IMessageBus } from '@tom-ui/utils'
import _ from 'lodash'
import { computed, makeObservable, observable, runInAction } from 'mobx'
import { BaseEntityStore } from './base/BaseEntityStore'

export interface HatchCoverFormData {
  id?: number
  equipmentId?: number | string
  carrierVisitId: number
  direction: HatchCoverMoveDirection
  operator: string
  at?: string
}

export class HatchCoverMoveStore extends BaseEntityStore<HatchCoverMoveDto> {
  selectedVisitId?: number
  visitEquipments: EquipmentPlannedDto[] = []

  constructor(private readonly messageBus: IMessageBus) {
    super()

    makeObservable(this, {
      visitEquipments: observable,

      cranes: computed,
      totalOpened: computed,
      totalClosed: computed,
    })

    this.messageBus.subscribeEvent<HatchCoverMoveDto>(
      EventTypes.HatchCoverMoveUpserted,
      this.handleUpsertedEvent,
    )
    this.messageBus.subscribeEvent<number>(
      EventTypes.HatchCoverMoveDeleted,
      this.handleDeletedEvent,
    )
  }

  public get cranes(): EquipmentPlannedDto[] {
    const cranes = this.visitEquipments.filter(x => x.equipmentType === EquipmentType.Sts)
    return _(cranes)
      .sortBy(x => x.name)
      .value()
  }

  public get totalOpened(): number {
    return this.items.filter(x => x.direction === HatchCoverMoveDirection.Open).length
  }

  public get totalClosed(): number {
    return this.items.filter(x => x.direction === HatchCoverMoveDirection.Close).length
  }

  public async fetchDataByVisitId(visitId: number) {
    if (visitId === this.selectedVisitId) return

    await Promise.all([this.loadRecordsByVisitId(visitId), this.loadEquipments(visitId)])
    this.selectedVisitId = visitId
  }

  public async loadRecordsByVisitId(vesselVisitId: number) {
    if (vesselVisitId === this.selectedVisitId) return

    const moves = await hatchCoverMoveService.getHatchCoverMovesByVesselVisitId(vesselVisitId)
    this.updateStoreItems(moves)
  }

  public async loadEquipments(vesselVisitId: number) {
    let equipments = await equipmentService.loadPlanned(vesselVisitId)

    //Load all cranes from terminal in case there is no planned cranes for the visit
    if (equipments.length === 0 || !equipments.some(e => e.equipmentType === EquipmentType.Sts)) {
      equipments = await equipmentService.load({
        includeInactive: true,
        equipmentType: EquipmentType.Sts,
      })
    }

    runInAction(() => {
      this.visitEquipments = equipments ?? []
    })
  }

  public onSubmit = async (data: HatchCoverFormData, gangId?: number) => {
    const isEditMode = !!data.id

    if (isEditMode) {
      const cmd: UpdateHatchCoverMoveCommand = {
        ...data,
        id: data.id!,
        equipmentId: data.equipmentId! as number,
      }
      await hatchCoverMoveService.update(cmd)
    } else {
      const cmd: CreateHatchCoverMoveCommand = {
        ...data,
        equipmentId: data.equipmentId! as number,
        gangId: gangId,
      }
      await hatchCoverMoveService.create(cmd)
    }
  }

  public onDelete = async (id: number) => {
    await hatchCoverMoveService.delete(id)
  }

  private readonly handleUpsertedEvent = (res: IEvent<HatchCoverMoveDto>) => {
    if (!res.payload) return

    this.updateStoreItem(res.payload, res.payload.id)
  }

  private readonly handleDeletedEvent = (res: IEvent<number>) => {
    if (!res.payload) return

    this.deleteStoreItem(res.payload)
  }
}
